/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Anomaly Recommendation Rules
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Pure rule engine. Given an anomaly row and the heavy-query / top-user
/// drill-down loaded by the investigation drawer, emit a prioritized list
/// of recommendations the operator can act on.
///
/// Each rule is independent, runs only when its preconditions match, and
/// never inspects state outside its inputs. Tests can be added by
/// constructing fixture inputs and asserting the rule fires (or doesn't).
/// New rules append a unique `id` so callers can dedupe / persist
/// dismissals later without breaking on shape changes.
///
/// Ordering: `buildAnomalyRecommendations` runs every rule, collects the
/// emitted recommendations, then sorts critical → warning → info so the
/// drawer renders the most actionable items first.

import type { AnomalyHeavyQuery, AnomalyTopUser } from '~/composables/useCostData'

export interface CostAnomaly {
  id: string
  timestamp: string
  type: 'spike' | 'unusual_pattern'
  severity: 'info' | 'warning' | 'critical'
  warehouse: string
  user: string
  cost: number
  expectedCost: number
  description: string
}

export type RecommendationSeverity = 'critical' | 'warning' | 'info'

export interface AnomalyRecommendation {
  /** Stable identifier. Used for keying in the template and for
   *  any future "dismissed recommendations" persistence. */
  id: string
  severity: RecommendationSeverity
  /** Headline — fits on one line at narrow widths. */
  title: string
  /** Multi-sentence explanation + suggested action. May reference
   *  specific queries or users by name. */
  body: string
  /**
   * Optional follow-up shortcut. The drawer renders this as a button
   * next to the recommendation. We keep the action shape narrow on
   * purpose — at the moment it's just a label + URL, but the
   * interface gives us room to grow into in-app actions later
   * (e.g. `{ kind: 'open-query-history-filter', query: {...} }`).
   */
  action?: { label: string, href: string }
}

// Severity sorting helper. Lower = more urgent so the natural sort
// order matches the drawer's "critical first" presentation.
const SEVERITY_RANK: Record<RecommendationSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
}

/** Common rule preconditions/derivations are computed once up front and
 *  passed to every rule, so individual rules stay short and fast. */
interface RuleContext {
  anomaly: CostAnomaly
  queries: AnomalyHeavyQuery[]
  users: AnomalyTopUser[]
  /** Sum of executionTime (seconds) across the top-10 query slice. */
  totalTopExecSec: number
  /** σ above baseline parsed from the description text. NaN when
   *  the description doesn't include the standard "(N.Nσ)" suffix
   *  (older anomaly rows / non-spike types). */
  sigma: number
}

function buildContext(
  anomaly: CostAnomaly,
  queries: AnomalyHeavyQuery[],
  users: AnomalyTopUser[],
): RuleContext {
  const totalTopExecSec = queries.reduce((acc, q) => acc + (q.execSeconds || 0), 0)
  // Description is built by anomaliesSql with "...above 14d baseline (X.Xσ)."
  // Pull the σ value out for the reasoning text without re-querying.
  const m = /([\d.]+)σ/.exec(anomaly.description || '')
  const sigma = m ? parseFloat(m[1]) : NaN
  return { anomaly, queries, users, totalTopExecSec, sigma }
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

/** A single query consumed > 50% of execution time among the top-10 — the
 *  spike was likely caused by one expensive query the operator can target. */
function ruleConcentratedCost(ctx: RuleContext): AnomalyRecommendation | null {
  const top = ctx.queries[0]
  if (!top || ctx.totalTopExecSec <= 0) return null
  const share = (top.execSeconds || 0) / ctx.totalTopExecSec
  if (share < 0.5) return null
  const pct = Math.round(share * 100)
  return {
    id: 'concentrated-cost',
    severity: 'critical',
    title: `One query consumed ${pct}% of compute time on this warehouse`,
    body:
      `Query ${top.queryId.slice(0, 8)}… by ${top.userName} ran for ${formatSeconds(top.execSeconds)} ` +
      `on a ${top.warehouseSize || 'unspecified-size'} warehouse. ` +
      `Optimizing this single query (review the explain plan, add filters, or push it to a smaller warehouse) ` +
      `would have prevented most of the spike.`,
    action: { label: 'Open query in Snowsight', href: snowsightQueryUrl(top.queryId) },
  }
}

/** Any query took longer than 30 minutes — almost always a missing
 *  predicate, full table scan, or runaway join. */
function ruleLongRunningQuery(ctx: RuleContext): AnomalyRecommendation | null {
  const long = ctx.queries.filter(q => q.execSeconds >= 30 * 60)
  if (!long.length) return null
  const worst = long[0]
  return {
    id: 'long-running-query',
    severity: 'warning',
    title: `${long.length} ${plural(long.length, 'query', 'queries')} ran longer than 30 minutes`,
    body:
      `Longest: ${formatSeconds(worst.execSeconds)} (${worst.userName}). ` +
      `Long runs typically indicate missing predicates, full table scans, or non-selective joins. ` +
      `Open the query profile in Snowsight to inspect bytes scanned vs. rows produced and look for the worst-performing pruning step.`,
    action: { label: 'Review query profile', href: snowsightQueryUrl(worst.queryId) },
  }
}

/** Top-10 average execution time is short but the warehouse is large —
 *  workload doesn't justify the size, so downsizing should preserve
 *  responsiveness while cutting credits roughly in half per notch. */
function ruleOversizedWarehouse(ctx: RuleContext): AnomalyRecommendation | null {
  if (!ctx.queries.length) return null
  const avgExec = ctx.totalTopExecSec / ctx.queries.length
  if (avgExec >= 30) return null
  // Use the modal warehouse size across the top queries (size can change
  // mid-day on an auto-scale cluster; we report what dominated).
  const sizeCounts = new Map<string, number>()
  for (const q of ctx.queries) {
    if (!q.warehouseSize) continue
    sizeCounts.set(q.warehouseSize, (sizeCounts.get(q.warehouseSize) || 0) + 1)
  }
  let dominantSize = ''
  let max = 0
  for (const [s, c] of sizeCounts) if (c > max) { max = c; dominantSize = s }
  if (!dominantSize || /X-?SMALL/i.test(dominantSize)) return null
  if (!/(LARGE|MEDIUM)/i.test(dominantSize)) return null
  return {
    id: 'oversized-warehouse',
    severity: 'info',
    title: `${ctx.anomaly.warehouse} may be oversized for this workload`,
    body:
      `Top-10 queries averaged ${avgExec.toFixed(1)}s on a ${dominantSize} warehouse. ` +
      `Short queries on a large warehouse pay for compute that's not being used. ` +
      `Consider downsizing one notch — Snowflake's per-credit cost halves with each step down.`,
  }
}

/** Significant work scheduled outside business hours. Heuristic: > 25%
 *  of top-query exec time started between 22:00 and 06:00 UTC. */
function ruleOffHoursActivity(ctx: RuleContext): AnomalyRecommendation | null {
  if (!ctx.queries.length || ctx.totalTopExecSec <= 0) return null
  let offHoursSec = 0
  for (const q of ctx.queries) {
    const h = parseHourUtc(q.startTime)
    if (h === null) continue
    if (h >= 22 || h < 6) offHoursSec += q.execSeconds || 0
  }
  const share = offHoursSec / ctx.totalTopExecSec
  if (share < 0.25) return null
  return {
    id: 'off-hours-activity',
    severity: 'info',
    title: `${Math.round(share * 100)}% of compute happened off-hours (22:00–06:00 UTC)`,
    body:
      `If this was scheduled batch work, that's expected — but it's worth confirming AUTO_SUSPEND ` +
      `is set tight (60s is fine for batch) so the warehouse isn't burning credits between jobs. ` +
      `If it wasn't scheduled, investigate which user ran the queries and whether their access should be restricted off-hours.`,
  }
}

/** A single user holds a majority of execution-time share — probably a
 *  human running ad-hoc queries that triggered the spike. */
function ruleSingleUserDominant(ctx: RuleContext): AnomalyRecommendation | null {
  const top = ctx.users[0]
  if (!top || top.execSharePct < 70) return null
  return {
    id: 'single-user-dominant',
    severity: 'warning',
    title: `${top.userName} drove ${top.execSharePct.toFixed(0)}% of compute on this warehouse`,
    body:
      `${top.userName} ran ${top.queryCount} ${plural(top.queryCount, 'query', 'queries')} ` +
      `accounting for the bulk of the spike. ` +
      `Reach out before tightening warehouse policies — a one-off heavy report or backfill is normal, ` +
      `but if this is recurring, consider giving them a smaller dedicated warehouse so their ad-hoc work ` +
      `doesn't show up in the shared budget.`,
  }
}

/** Many top queries failed but still consumed credits. */
function ruleHighFailureRate(ctx: RuleContext): AnomalyRecommendation | null {
  if (!ctx.queries.length) return null
  const failed = ctx.queries.filter(q => q.executionStatus === 'FAIL' || q.executionStatus === 'FAILED_WITH_ERROR')
  if (failed.length / ctx.queries.length < 0.3) return null
  return {
    id: 'high-failure-rate',
    severity: 'warning',
    title: `${failed.length} of the top ${ctx.queries.length} queries failed`,
    body:
      `Failed queries still bill for the compute they consumed before erroring. ` +
      `Common causes: timeouts, statement-size limits, permissions, transient warehouse errors. ` +
      `Check the QUERY_HISTORY error messages for the failed runs and consider a STATEMENT_TIMEOUT cap on this warehouse to prevent runaway billing on broken jobs.`,
  }
}

/** Any single query scanned more than 100 GB — usually missing predicates
 *  or a join that exploded. */
function ruleHugeScan(ctx: RuleContext): AnomalyRecommendation | null {
  const ONE_HUNDRED_GB = 100 * 1024 ** 3
  const big = ctx.queries.filter(q => q.bytesScanned >= ONE_HUNDRED_GB)
  if (!big.length) return null
  const worst = big[0]
  return {
    id: 'huge-scan',
    severity: 'warning',
    title: `${big.length} ${plural(big.length, 'query', 'queries')} scanned more than 100 GB`,
    body:
      `Largest scan: ${formatBytes(worst.bytesScanned)} (${worst.userName}). ` +
      `Big scans are usually a sign of missing partition predicates, lack of clustering keys, or ` +
      `joins on un-pruned tables. Check the query profile — if the table is large and queried often, ` +
      `adding a clustering key on the predicate column tends to pay back fast.`,
    action: { label: 'Inspect biggest scan', href: snowsightQueryUrl(worst.queryId) },
  }
}

/** Cloud-services credits are unusually high — typically caused by
 *  metadata-heavy / repeatedly-cached queries from BI tools. */
function ruleHeavyCloudServices(ctx: RuleContext): AnomalyRecommendation | null {
  const csTotal = ctx.queries.reduce((acc, q) => acc + (q.cloudServicesCost || 0), 0)
  if (csTotal < 5) return null
  return {
    id: 'cloud-services-heavy',
    severity: 'info',
    title: `Cloud-services credits totaled $${csTotal.toFixed(2)} on the top queries`,
    body:
      `Cloud services charges (above the 10% daily allotment) most often come from BI tools or notebooks ` +
      `that re-issue identical metadata-heavy queries. Look for repeated SHOW / DESCRIBE / INFORMATION_SCHEMA ` +
      `lookups or for tools that bypass result caching. Caching commonly-issued metadata queries client-side, ` +
      `or enabling Snowflake's result cache, generally cuts these credits sharply.`,
  }
}

/** Always-include reminder for the operator about resource monitors —
 *  posted as a low-severity info card at the bottom of the list. */
function ruleResourceMonitorReminder(ctx: RuleContext): AnomalyRecommendation | null {
  return {
    id: 'resource-monitor-reminder',
    severity: 'info',
    title: 'Set a resource monitor on this warehouse',
    body:
      `If you don't already have one, create a daily / weekly / monthly resource monitor on ` +
      `${ctx.anomaly.warehouse} with a SUSPEND action at the budget you've committed to. Monitors ` +
      `are the most reliable way to bound the blast radius of unexpected spikes — they'll suspend the ` +
      `warehouse before billing runs away while you investigate.`,
  }
}

const RULES = [
  ruleConcentratedCost,
  ruleLongRunningQuery,
  ruleOversizedWarehouse,
  ruleOffHoursActivity,
  ruleSingleUserDominant,
  ruleHighFailureRate,
  ruleHugeScan,
  ruleHeavyCloudServices,
  ruleResourceMonitorReminder,
]

/**
 * Run every rule against the supplied context. Rules that return null
 * are skipped. Output is sorted critical → warning → info; ties keep
 * insertion order (which matches the rule list, ranked by usefulness).
 */
export function buildAnomalyRecommendations(
  anomaly: CostAnomaly,
  queries: AnomalyHeavyQuery[],
  users: AnomalyTopUser[],
): AnomalyRecommendation[] {
  const ctx = buildContext(anomaly, queries, users)
  const out: AnomalyRecommendation[] = []
  for (const rule of RULES) {
    const r = rule(ctx)
    if (r) out.push(r)
  }
  return out.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function plural(n: number, singular: string, pluralForm: string): string {
  return n === 1 ? singular : pluralForm
}

function formatSeconds(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return '0s'
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  const rem = Math.round(s - m * 60)
  if (m < 60) return `${m}m ${rem}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m - h * 60}m`
}

function formatBytes(b: number): string {
  if (!Number.isFinite(b) || b <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let v = b
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`
}

/** ISO timestamp → UTC hour. Returns null for unparseable input so
 *  rules can skip the row rather than poison the heuristic. */
function parseHourUtc(iso: string): number | null {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return new Date(t).getUTCHours()
}

/** Build a Snowsight URL that pre-fills the query history filter on a
 *  specific QUERY_ID. Snowsight's deep-link format is stable enough to
 *  be useful as an action target; if the Snowflake account URL isn't
 *  what we expected, the user just lands on Snowsight's home and can
 *  paste the QUERY_ID manually. */
function snowsightQueryUrl(queryId: string): string {
  // Generic Snowsight QUERY_ID link — the user's Snowflake org redirects
  // to the right account when they're authenticated.
  return `https://app.snowflake.com/_/#/compute/history/queries/${encodeURIComponent(queryId)}`
}
